import React, { useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, SkipForward } from 'lucide-react';
import { ACTIONS, formatINR } from './payoutUtils';

const TITLES = {
  approve: ['Approve Selected Payouts?', 'This action will move eligible payouts to the next processing state.', 'Confirm Approval'],
  reject: ['Reject Selected Payouts?', 'Rejected payouts release held funds back to the merchant wallet.', 'Reject Selected'],
  processing: ['Move Payouts to Processing?', 'Only approved (or failed) payouts are eligible.', 'Confirm'],
  cancel: ['Cancel Selected Payouts?', 'Cancelled payouts will follow the existing wallet/ledger reversal rules.', 'Confirm Cancellation'],
  retry: ['Retry Failed Payouts?', 'Eligible failed payouts will be re-executed across the remaining active rails.', 'Retry Selected'],
};

// Bulk-action confirmation for status transitions. Completion ("paid") is NOT handled here — it requires BulkSettlementModal (proof).
export const BulkActionDialog = ({ action, payouts, open, onOpenChange, onDone }) => {
  const { api } = useAuth();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const meta = action && TITLES[action] ? ACTIONS[action] : null;
  const { eligible, skipped } = useMemo(() => {
    if (!meta) return { eligible: [], skipped: [] };
    const e = payouts.filter(p => meta.from.includes(p.status));
    return { eligible: e, skipped: payouts.filter(p => !meta.from.includes(p.status)) };
  }, [payouts, meta]);
  const total = eligible.reduce((s, p) => s + (p.amount || 0), 0);

  const reset = () => { setReason(''); setResult(null); };
  const close = (o) => { if (!o) reset(); onOpenChange(o); };

  const run = async () => {
    if (meta.needsReason && !reason.trim()) return toast.error('Reason is required');
    setBusy(true);
    try {
      const key = `bulk-${action}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const r = await api.post('/payouts/admin/bulk-action',
        { payoutIds: eligible.map(p => p.id), action, reason: reason || null },
        { headers: { 'Idempotency-Key': key } });
      setResult(r.data);
      toast.success(`${r.data.processed} processed · ${r.data.skipped + skipped.length} skipped`);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk action failed');
    } finally { setBusy(false); }
  };

  if (!meta) return null;
  const [title, note, cta] = TITLES[action];
  const tone = meta.tone === 'danger' ? 'bg-red-600 hover:bg-red-700 text-white' : meta.tone === 'success' ? 'bg-green-600 hover:bg-green-700 text-white' : '';

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="rounded-sm max-w-lg" data-testid="bulk-dialog">
        <DialogHeader>
          <DialogTitle data-testid="bulk-dialog-title">{result ? 'Bulk Action Result' : title}</DialogTitle>
          <DialogDescription>{result ? `${result.processed} successful · ${result.skipped + skipped.length} skipped` : note}</DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div className="p-3 rounded-sm border border-border/60"><p className="text-xs text-muted-foreground">Selected</p><p className="font-semibold" data-testid="bulk-selected-count">{payouts.length}</p></div>
              <div className="p-3 rounded-sm border border-green-500/30 bg-green-500/5"><p className="text-xs text-muted-foreground">Eligible</p><p className="font-semibold text-green-600" data-testid="bulk-eligible-count">{eligible.length}</p></div>
              <div className="p-3 rounded-sm border border-border/60"><p className="text-xs text-muted-foreground">Skipped</p><p className="font-semibold" data-testid="bulk-skipped-count">{skipped.length}</p></div>
            </div>
            <div className="flex items-center justify-between p-3 rounded-sm bg-muted/40 text-sm"><span className="text-muted-foreground">Total Amount (eligible)</span><span className="font-semibold font-mono" data-testid="bulk-total-amount">{formatINR(total)}</span></div>
            {skipped.length > 0 && (
              <p className="text-xs text-muted-foreground flex items-start gap-1.5"><SkipForward className="h-3.5 w-3.5 mt-0.5 shrink-0" />Skipped (not eligible from current status): {skipped.map(p => p.order_id).join(', ')}</p>
            )}
            {meta.needsReason && (
              <div className="space-y-1.5"><Label>Reason *</Label><Textarea rows={3} value={reason} onChange={e => setReason(e.target.value)} className="rounded-sm" placeholder="Required — recorded on every affected payout" data-testid="bulk-reason-input" /></div>
            )}
          </div>
        ) : (
          <div className="max-h-72 overflow-y-auto space-y-1 text-sm" data-testid="bulk-results">
            {result.results.map(r => (
              <div key={r.payout_id} className="flex items-center justify-between gap-3 py-1.5 border-b border-border/40">
                <span className="font-mono text-xs">{r.order_id || r.payout_id}</span>
                {r.status === 'processed'
                  ? <span className="text-green-600 text-xs inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" />{r.new_status}</span>
                  : <span className="text-muted-foreground text-xs text-right">Skipped · {r.reason}</span>}
              </div>
            ))}
            {skipped.map(p => (
              <div key={p.id} className="flex items-center justify-between gap-3 py-1.5 border-b border-border/40"><span className="font-mono text-xs">{p.order_id}</span><span className="text-muted-foreground text-xs">Skipped · not eligible ({p.status})</span></div>
            ))}
          </div>
        )}

        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" onClick={() => close(false)} className="rounded-sm" data-testid="bulk-cancel-btn">Cancel</Button>
              <Button onClick={run} disabled={busy || eligible.length === 0} className={`rounded-sm ${tone}`} data-testid="bulk-confirm-btn">
                {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{cta}{eligible.length ? ` (${eligible.length})` : ''}
              </Button>
            </>
          ) : <Button onClick={() => close(false)} className="rounded-sm" data-testid="bulk-close-btn">Close</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
